import React, { useState, useEffect } from "react";
import { Col, Row, Form, Button, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getAuth, RecaptchaVerifier } from "firebase/auth";
import UserPhone from "./account_phone";
import AccountVerification from "./account_verification";
import { userSelector } from "../../../store/user/user.selector";
import { linkWithPhone, unlinkPhone } from "../../../store/auth/auth.action";
import { updateUserInfo } from "../../../store/user/user.action";

const wrapperCol = {
  xs: {
    span: 24,
    offset: 0,
  },
  sm: {
    span: 18,
    offset: 0,
  },
};

const labelCol = {
  xs: {
    span: 24,
    offset: 0,
  },
  sm: {
    span: 6,
    offset: 0,
  },
};

const resetData = (user, form) => {
  const values = {
    phone: user?.phone || "",
  };
  form.setFieldsValue(values);
};

const AccountFormMobile = () => {
  // -------------------------- VAR --------------------------
  const [form] = Form.useForm();
  const auth = getAuth();
  // auth.settings.appVerificationDisabledForTesting = true;
  const { t } = useTranslation("translation", { keyPrefix: "account" });
  // -------------------------- STATE --------------------------
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  // -------------------------- REDUX --------------------------
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  // -------------------------- FUNCTION --------------------------

  const handleEdit = () => {
    resetData(user, form);
    setEdit(true);
  };

  const handleCancel = () => {
    resetData(user, form);
    setEdit(false);
  };

  // -------------------------- EFFECT --------------------------

  useEffect(() => {
    if (edit) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        "verification-button",
        {
          size: "invisible",
          callback: (response) => {
            console.log({ response });
          },
        },
        auth
      );
    }
  }, [auth, edit]);

  useEffect(() => {
    resetData(user, form);
  }, [user, form]);

  // -------------------------- DATA FUNCTION --------------------------

  const onVerification = (values) => {
    const verifier = window.recaptchaVerifier;
    const phone = values.phone;
    const oldPhone = auth.currentUser.phoneNumber;
    if (phone === oldPhone) {
      message.error("Old phone number");
      return false;
    }
    setLoading(true);
    dispatch(
      unlinkPhone((res) => {
        if (res.status === 200) {
          dispatch(
            linkWithPhone({ phone, verifier }, (response) => {
              setLoading(false);
              if (response?.status === 200) {
                const result = response?.data;
                message.success("Code sent successfully!");
                setResult(result);
                setStep(1);
              }
              if (response?.status === 500) {
                message.error(response?.data || "Failed!");
              }
            })
          );
        } else if (res.status === 500) {
          setLoading(false);
          message.error(res?.data || "Failed!");
        }
      })
    );
  };

  const onConfirmation = (values) => {
    setLoading(true);
    const code = values.code;
    result
      ?.confirm(code)
      .then(() => {
        setLoading(false);
        setStep(0);
        const phone = auth.currentUser.phoneNumber;
        const update = { phone };
        dispatch(
          updateUserInfo({ update }, (response) => {
            setLoading(false);
            if (response?.status === 200) {
              message.success("Update Account Details successfully!");
              setEdit(false);
              setStep(0);
            }
            if (response?.status === 500) {
              message.error(response?.data || "Failed");
            }
          })
        );
      })
      .catch((error) => {
        setLoading(false);
        message.error(error.message);
      });
  };

  // -------------------------- RENDER --------------------------

  const renderForm = () => {
    const disabled = loading || !edit;
    if (step === 0) {
      return (
        <Form
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          name="mobile_form"
          autoComplete="off"
          form={form}
          onFinish={onVerification}
        >
          <Row gutter={[16, 0]}>
            <Col xl={24} lg={24} md={24} xs={24}>
              <UserPhone disabled={disabled} />
            </Col>
            <Col xl={24} lg={24} md={24} xs={24}>
              {renderEditButton()}
              {renderSaveButton()}
            </Col>
          </Row>
        </Form>
      );
    }
  };

  const renderEditButton = () => {
    if (!edit) {
      return (
        <Row gutter={[16, 5]} justify="end" align="center">
          <Col>
            <Button
              type="primary"
              className="login-form-button form-button"
              loading={loading}
              onClick={() => handleEdit()}
            >
              {t("edit")}
            </Button>
          </Col>
        </Row>
      );
    }
    return null;
  };

  const renderSaveButton = () => {
    if (edit) {
      return (
        <Row gutter={[16, 5]} justify="end" align="center">
          <Col>
            <Button
              className="login-form-button form-button"
              loading={loading}
              onClick={() => handleCancel()}
            >
              {t("cancel")}
            </Button>
          </Col>
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                className="login-form-button form-button save"
                id="verification-button"
                loading={loading}
              >
                {t("save")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      );
    }
    return null;
  };

  const renderConfirmationForm = () => {
    const disabled = loading;
    if (step === 1) {
      return (
        <Form name="confirmation_form" autoComplete="off" onFinish={onConfirmation}>
          <Row gutter={[16, 0]}>
            <Col xl={24} lg={24} md={24} xs={24}>
              <AccountVerification disabled={disabled} />
            </Col>
            <Col xl={24} lg={24} md={24} xs={24}>
              <Row gutter={[16, 5]} justify="end" align="center">
                <Col>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="login-form-button form-button confirm"
                      loading={loading}
                    >
                      {t("confirm")}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      );
    }
  };

  // -------------------------- MAIN --------------------------

  return (
    <Row gutter={[16, 5]}>
      <Col xl={24} lg={24} md={24} xs={24}>
        {renderForm()}
        {renderConfirmationForm()}
      </Col>
    </Row>
  );
};

export default AccountFormMobile;
